import { AxiosError, HttpStatusCode } from 'axios';

import { I18nResources, useI18n } from '@app-velocity/i18n';

import { ErrorWithDetails } from '@app-velocity/common';

import { Alert } from '../Alert';
import { ButtonsContainer } from '../ButtonsContainer';

export type ErrorMessages = Partial<
  Record<HttpStatusCode, I18nResources | undefined>
>;

export type ErrorMessageProps = {
  error: string | AxiosError | Error | ErrorWithDetails | null;
  statusMap?: {
    [status: string]: string;
  };
};

type SystemError = {
  response: {
    data: {
      code: number;
      details: string;
    };
  };
};

const isCorrectSystemResponse = (error: unknown): error is SystemError => {
  const correctError = error as SystemError;
  if (
    'response' in correctError &&
    'data' in correctError.response &&
    'code' in correctError.response.data &&
    typeof correctError.response.data.code === 'number'
  )
    return true;

  return false;
};

export const ErrorMessage = ({ error, statusMap }: ErrorMessageProps) => {
  const { t } = useI18n();

  const getErrorByCode = (error: SystemError): string | null => {
    switch (error.response.data.code) {
      case 2004:
        return t('users:errors.email_already_taken');
      case 2005:
        return t('users:errors.username_already_taken');
      case 2006:
        return t('prototypes:errors.scenario_already_exists');
      case 4001:
        return t('users:errors.not_authorized');
      case 4002:
        return t('users:errors.not_authorized');
      case 4003:
        return t('users:errors.not_authorized');
      case 4004:
        return t('users:errors.account_is_not_active');
      case 5001:
        return t('config:errors.ai_connection_lost');
      case 6001:
        return t('shared:errors.validation_error');
      case 6002:
        return t('shared:errors.maximum_upload_size_exceeded');
      case 6003:
        return t('shared:errors.maximum_image_size_exceeded');
      case 7001:
        return t('users:errors.invalid_credentials');
      default:
        return error.response.data.details;
    }

    return null;
  };

  const getMessage = () => {
    if (error === null) {
      return null;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (
      error instanceof AxiosError &&
      statusMap &&
      error.status &&
      statusMap[error.status]
    ) {
      return statusMap[error.status];
    }

    if (isCorrectSystemResponse(error)) {
      const res = getErrorByCode(error);

      return res ? res : null;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return t('shared:errors.something_went_wrong');
  };

  if (!error) return null;

  const navigateToErrorUrl = (url: string) => {
    window.open(url);
  };

  if (error instanceof ErrorWithDetails) {
    return (
      <Alert severity="error" title={error.message}>
        {error.details}
        {error.actionLabel && error.actionUrl ? (
          <ButtonsContainer>
            <Alert.ActionButton
              status="error"
              onClick={() => navigateToErrorUrl(error.actionUrl!)}
            >
              {t(error.actionLabel as I18nResources)}
            </Alert.ActionButton>
          </ButtonsContainer>
        ) : null}
      </Alert>
    );
  }

  return <Alert severity="error">{getMessage()}</Alert>;
};
